/* eslint-disable max-lines */
import { combineLatest, Observable, zip } from 'rxjs';
import { select, Store } from '@ngrx/store';
import { Injectable } from '@angular/core';
import { map, take, tap } from 'rxjs/operators';
import { FlightType } from '@search-bar/entities/flight-type';
import { PassengerArrangement } from '@shared/base-search-bar/entities/passenger-arrangement';
import { FlightClass } from '@shared/base-search-bar/entities/flight-class';
import { State } from '@app/store/state/state';
import { SearchData } from '@shared/base-search-bar/entities/search-data';
import { PlaceType } from '@shared/base-search-bar/entities/place-type';
import {
  loadSearchDataAction, saveSearchDataAction, saveExchangeSearchDataAction, resetAlertsDataAction
} from '@shared/base-search-bar/store/actions/storage.actions';
import { SelectAutocompletePlace } from '@search-bar/entities/select-autocomplete-place';
import { SelectAutocompletePrediction } from '@search-bar/entities/select-autocomplete-prediction';
import * as actions from '@shared/base-search-bar/store/actions/search-bar.actions';
import { Room } from '@shared/base-search-bar/entities/room.entities';
import { SearchType } from '@search-bar/entities/search-type';
import { resetPackageSelectedOptionsAction } from '@packages/packages-result/store/actions/packages-result.actions';
import { BaseSearchBarFacadeAbstract } from '@shared/base-search-bar/abstract/base-search-bar-facade.abstract';
import { toggleAutoDiffPlace } from '@shared/base-search-bar/store/actions/search-bar.actions';
import {
  DestinationPlaceInfoPayload, InputDestinationPlace
} from '@shared/base-search-bar/entities/autocomplete-destionation';
import { SearchBy } from '@shared/base-search-bar/entities/search.entities';
import { HeaderProductType } from '@header/entities/ds-header-config';
import { setEditSearchByProductAction } from '@app/store/actions/global.actions';
import { getUIsearchBarSelector } from '@shared/base-search-bar/store/selectors/ui.selector';
import { InputTravelExtra, TravelExtraActivityInfoPayload } from '@shared/base-search-bar/entities/autocomplete-travel-extra';

@Injectable()
export class BaseSearchBarFacade extends BaseSearchBarFacadeAbstract {
  
  constructor(
    protected store: Store<State>,
  ) {
    super(store);
  }

  public editSearch$: Observable<HeaderProductType> = this.store.pipe(
    select(getUIsearchBarSelector),
    map(state => state?.editSearch)
  );

  public editSearchIsActivated$(product: HeaderProductType): Observable<boolean> {
    return this.store.pipe(
      select(getUIsearchBarSelector),
      map(state => !!state?.editSearch && state?.editSearch === product)
    );
  }

  public setEditSearchByProduct(current: HeaderProductType, next: HeaderProductType): void {
    const product = current === next ? null : next;
    this.store.dispatch(setEditSearchByProductAction({ product }));
  }

  get searchData$(): Observable<SearchData> {
    return combineLatest([
      this.departurePlace$, this.arrivalPlace$, this.departureDate$, this.arrivalDate$, this.passengerArrangements$,
      this.pickUpPlace$, this.dropOffPlace$, this.pickUpTime$, this.dropOffTime$, this.flightClass$, this.flightType$,
      this.defaultPlace$, this.passengerArrangementsState$, this.airline$, this.guestsPerRoomState$, this.guestsPerRoom$,
      this.baggageIncluded$, this.branchCode$, this.destinationPlace$, this.departureTerminal$, this.arrivalTerminal$,
      this.salesChannel$, this.packageStep$, this.hotelDestinationPlace$, this.hourType$,
      this.packageDestinationPlace$, this.packageOriginPlace$, this.inputTravelExtra$,
    ]).pipe(
      map(([
        departurePlace, arrivalPlace, departureDate, arrivalDate, passengerArrangement,
        pickUpPlace, dropOffPlace, pickUpTime, dropOffTime, flightClass, flightType, defaultPlaceData,
        passengerArrangementsState, airline, guestsPerRoomState, guestsPerRoom, baggageIncluded, branchCode, destinationPlace,
        departureTerminal, arrivalTerminal, salesChannel, packageStep, hotelDestinationPlace, hourType,
        packageDestinationPlace, packageOriginPlace, inputTravelExtra
      ]) => {
        const defaultPlace = {
          description: defaultPlaceData?.description,
          code: defaultPlaceData?.code,
          highlight_description: defaultPlaceData?.highlight_description,
          country: defaultPlaceData?.country,
          default: true
        };
        return {
          departurePlace: departurePlace?.code ? departurePlace : defaultPlace,
          arrivalPlace,destinationPlace, departureDate, arrivalDate, passengerArrangement,
          flightType, passengerArrangementsState, airline, flightClass, salesChannel, packageStep,
          pickUpPlace, dropOffPlace, pickUpTime, dropOffTime, guestsPerRoomState, guestsPerRoom,
          baggageIncluded, branchCode, departureTerminal, arrivalTerminal, hotelDestinationPlace, hourType,
          packageDestinationPlace, packageOriginPlace, inputTravelExtra
        };
      })
    );
  }

  public fetchDefaultPlace(searchType: SearchType): void {
    this.store.dispatch(actions.fetchDefaultPlaceAction({ searchType: searchType }));
  }

  public selectPlace(place: SelectAutocompletePlace, isDestination: boolean = false, searchType?: SearchType): void {
    if (searchType === SearchType.HOME_BUSES || searchType === SearchType.BUSES_RESULTS) {
      this.selectTerminal(place);
    } else {
      this.selectPlaces(place, isDestination);
    }
  }

  private selectPlaces(place: SelectAutocompletePlace, isDestination: boolean): void {
    if (place.type === PlaceType.DEPARTURE) {
      this.store.dispatch(actions.selectDeparturePlaceAction({ selectedPlace: place }));
    } else {
      if (isDestination) this.store.dispatch(actions.selectDestinationPlaceAction({ selectedPlace: place }));
      else this.store.dispatch(actions.selectArrivalPlaceAction({ selectedPlace: place }));
    }
  }

  public selectAutoPlaces(place: SelectAutocompletePrediction): void {
    if (place.type === PlaceType.DEPARTURE) {
      this.selectAutoPickUpPlace(place);
    } else {
      this.selectAutoDropOffPlace(place);
    }
  }

  public selectAutoPickUpPlace(place: SelectAutocompletePrediction): void {
    this.store.dispatch(actions.selectPickUpPlaceAction({ place: place }));
  }

  public selectAutoDropOffPlace(place: SelectAutocompletePrediction): void {
    this.store.dispatch(actions.selectDropOffPlaceAction({ place: place }));
  }

  private selectTerminal(place: SelectAutocompletePlace): void {
    if (place.type === PlaceType.DEPARTURE) {
      this.store.dispatch(actions.selectDepartureTerminalAction({ place: place }));
    } else {
      this.store.dispatch(actions.selectArrivalTerminalAction({ place: place }));
    }
  }

  public selectAirline(airlineCode: string): void {
    this.store.dispatch(actions.selectAirlineAction({ airlineCode }));
  }

  public selectFlightType(type: FlightType): void {
    this.store.dispatch(actions.selectFlightTypeAction({ flightType: type }));
  }

  public selectFlightClass(flightClass: FlightClass): void {
    this.store.dispatch(actions.selectFlightClassAction({ flightClass: flightClass }));
  }

  public selectHourType(hourType: string[]): void {
    this.searchData$.pipe(
      take(1),
      tap(searchData => (
        this.store.dispatch(saveSearchDataAction({data: {...searchData, hourType }, searchBy: SearchBy.HOTELS}))
      )) 
    ).subscribe().unsubscribe();
    this.store.dispatch(actions.selectHourTypeAction({ hourType }));
  }

  public selectCalendarDates(calendar: Date[]): void {
    const [departure, arrival] = calendar;
    this.store.dispatch(actions.selectDepartureDateAction({ date: departure }));
    this.store.dispatch(actions.selectArrivalDateAction({ date: arrival }));
  }

  public selectCalendarTimes(times: string[]): void {
    const [pickUpTime, dropOffTime] = times;
    this.store.dispatch(actions.selectPickUpTimeAction({ time: pickUpTime }));
    this.store.dispatch(actions.selectDropOffTimeAction({ time: dropOffTime }));
  }

  public setPassengerArrangement(passengerArrangement: PassengerArrangement): void {
    this.store.dispatch(actions.selectPassengerArrangementAction({ passengerArrangement: passengerArrangement }));
  }

  public setGuestsPerRoom(guestsPerRoom: Room): void {
    this.store.dispatch(actions.selectGuestsPerRoomAction({ guestsPerRoom }));
  }

  public resetGuestsPerRoom(): void {
    this.store.dispatch(actions.resetGuestsPerRoomAction());
  }

  public addNewRoom(room: Room): void {
    this.store.dispatch(actions.addRoomAction({ room }));
  }

  public deleteRoom(index: number): void {
    this.store.dispatch(actions.deleteRoomAction({ index }));
  }

  public saveSearchData(data: SearchData, searchBy: SearchBy): void {
    this.store.dispatch(saveSearchDataAction({ data, searchBy }));
  }

  public saveExchangeSearchData(data: SearchData): void {
    this.store.dispatch(saveExchangeSearchDataAction({ data: data }));
  }

  public resetAlertsData(): void {
    this.store.dispatch(resetAlertsDataAction());
  }

  public loadSearchData(searchBy: SearchBy): void {
    this.store.dispatch(loadSearchDataAction({ searchBy }));
  }

  public resetAutocomplete(): void {
    this.store.dispatch(actions.resetAutocomplete());
  }

  public swapPlaces(searchType: SearchType, isPackages: boolean = false): void {
    if (searchType === SearchType.BUSES_RESULTS || searchType === SearchType.HOME_BUSES) this.swapTerminals();
    else this.swapPlacesDefault(isPackages);
  }

  public swapPlacesDefault(isPackages: boolean): void {
    zip(this.departurePlace$.pipe(take(1)), this.arrivalPlace$.pipe(take(1)), this.defaultPlace$.pipe(take(1)),
      this.destinationPlace$.pipe(take(1))).subscribe(([departurePlace, arrivalPlace, defaultPlace, destinationPlace]) => {
      if (isPackages && !('city' in departurePlace && 'city' in destinationPlace)) {
        this.store.dispatch(actions.fetchDefaultPlaceActionSuccess({
          place: { ...defaultPlace, type: defaultPlace.type === PlaceType.ARRIVAL ? PlaceType.DEPARTURE : PlaceType.ARRIVAL }
        }));
        const destination = departurePlace.code ? departurePlace : {} as SelectAutocompletePlace;
        const departure = destinationPlace.code ? destinationPlace : {} as SelectAutocompletePlace;
        this.selectPlace({ ...destination, type: PlaceType.ARRIVAL }, true);
        this.selectPlace({ ...departure, type: PlaceType.DEPARTURE });
      } else if (!('city' in departurePlace && 'city' in arrivalPlace)) {
        this.store.dispatch(actions.fetchDefaultPlaceActionSuccess({
          place: { ...defaultPlace, type: defaultPlace.type === PlaceType.ARRIVAL ? PlaceType.DEPARTURE : PlaceType.ARRIVAL }
        }));
        const arrival = departurePlace.code ? departurePlace : {} as SelectAutocompletePlace;
        const departure = arrivalPlace.code ? arrivalPlace : {} as SelectAutocompletePlace;
        this.selectPlace({ ...arrival, type: PlaceType.ARRIVAL });
        this.selectPlace({ ...departure, type: PlaceType.DEPARTURE });
      }
    }).unsubscribe();
  }

  public swapTerminals(): void {
    zip(
      this.departureTerminal$.pipe(take(1)),
      this.arrivalTerminal$.pipe(take(1)),
      this.defaultPlace$.pipe(take(1))
    ).subscribe(data => {
      if (!('city' in data[0] && 'city' in data[1])) {
        this.store.dispatch(actions.fetchDefaultPlaceActionSuccess({
          place: { ...data[2], type: data[2].type === PlaceType.ARRIVAL ? PlaceType.DEPARTURE : PlaceType.ARRIVAL }
        }));
      }
      const arrival = data[0].code ? data[0] : {} as SelectAutocompletePlace;
      const departure = data[1].code ? data[1] : {} as SelectAutocompletePlace;
      this.selectPlace({ ...arrival, type: PlaceType.ARRIVAL }, false, SearchType.BUSES_RESULTS);
      this.selectPlace({ ...departure, type: PlaceType.DEPARTURE }, false, SearchType.BUSES_RESULTS);
    }).unsubscribe();
  }

  public searchPlaces(term: string): void {
    this.store.dispatch(actions.searchPlacesAction({ term }));
  }

  public includeBaggage(included: boolean): void {
    this.store.dispatch(actions.includeBaggageAction({ included }));
  }

  public resetBranchCode(): void {
    this.store.dispatch(actions.resetBranchCodeAction());
  }

  public setBranchCode(branchCode: string): void {
    this.store.dispatch(actions.setBranchCodeAction({ branchCode }));
  }

  public resetSalesChannel(): void {
    this.store.dispatch(actions.resetSalesChannelAction());
  }

  public setSalesChannel(code: string): void {
    this.store.dispatch(actions.setSalesChannelAction({ code }));
  }

  public setPackageStep(code: string): void {
    this.store.dispatch(actions.setPackageStepAction({ code }));
  }
  public resetPackageStep(): void {
    this.store.dispatch(actions.resetPackageStepAction());
  }

  public resetPackageSelectedOptions(): void {
    this.store.dispatch(resetPackageSelectedOptionsAction());
  }

  public toggleHeaderEditButtonVisible(visible: boolean): void {
    this.store.dispatch(actions.toggleHeaderEditButtonVisibleAction({ value: visible }));
  }

  public toggleAutoDiffPlace(): void {
    this.store.dispatch(toggleAutoDiffPlace());
  }

  public resetArrivalPlace(): void {
    this.store.dispatch(actions.resetInputArrivalPlace());
  }

  public searchDestinationPlaces(query: string): void {
    this.store.dispatch(actions.searchDestinationPlacesAction({ query }));
  }

  public selectHotelDestinationPlace(selection: InputDestinationPlace): void {
    this.store.dispatch(actions.selectHotelDestinationPlaceAction({ selection }));
  }

  public searchDestinationPlaceInfo(payload: DestinationPlaceInfoPayload): void {
    this.store.dispatch(actions.searchDestinationPlaceInfoAction({ payload }));
  }

  public clearSearchBarDataAction(): void {
    this.store.dispatch(actions.clearSearchBarDataAction());
  }

  public setSkipServices(value: boolean): void {
    this.store.dispatch(actions.setSkipServices(value));
  }

  public setTabSelectedHome(value: SearchType): void {
    this.store.dispatch(actions.setTabSelectedHome(value));
  }

  public selectPackageDestinationPlace(selection: InputDestinationPlace): void {
    this.store.dispatch(actions.selectPackageDestinationPlaceAction({ selection }));
  }

  public selectPackageOriginPlace(selection: InputDestinationPlace): void {
    this.store.dispatch(actions.selectPackageOriginPlaceAction({ selection }));
  }

  public searchExtraByQuery(query: string): void {
    this.store.dispatch(actions.searchExtraByQueryAction({ query }));
  }

  public selectTravelExtraInput(data: InputTravelExtra): void {
    this.store.dispatch(actions.selectTravelExtraInputAction({ selection: data }));
  }

  public searchTravelExtraActivityInfo(payload: TravelExtraActivityInfoPayload): void {
    this.store.dispatch(actions.searchTravelExtraActivityInfoAction({ payload }));
  }
}
